import React, { useCallback, useEffect, useState } from 'react';
import {
  Webhook,
  Copy,
  Check,
  ShieldCheck,
  ShieldAlert,
  Send,
  RefreshCw,
  ExternalLink,
  CircleSlash,
  ListChecks,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useI18n } from '@/i18n/LanguageContext';
import {
  PAYMENT_WEBHOOK_URL,
  WEBHOOK_METHOD,
  WEBHOOK_CONTENT_TYPE,
  WEBHOOK_HEADER_NAME,
  WEBHOOK_SECRET_ENV,
  WEBHOOK_HEADERS,
  WEBHOOK_PLAN_CREDITS,
  WEBHOOK_PLAN_CENTS,
  SUCCESS_EVENT_TYPES,
  RENEWAL_EVENT_TYPES,
  NON_GRANTING_EVENT_TYPES,
  REQUIRED_WEBHOOK_FIELDS,
  OPTIONAL_WEBHOOK_FIELDS,
  CRM_SETUP_STEPS,
  webhookPayloadTemplate,
  returnUrlFor,
} from '@/data/webhookSetup';

import { money } from '@/data/constants';
import { brandCase } from '@/lib/brand';


/**
 * Admin > Webhook & recovery (top half).
 *
 * Shows the EXACT values an operator pastes into the CRM, each with a copy
 * button, plus a live probe of the edge-function configuration (is the shared
 * secret set? what is its fingerprint?) and a one-click SAFE delivery test
 * that proves the endpoint answers 200 without charging a card or granting a
 * credit to anybody.
 *
 * Every value rendered here comes from src/data/webhookSetup.ts so the docs,
 * the UI and the edge function can never drift apart.
 */

const CopyRow: React.FC<{ label: string; value: string; mono?: boolean; multiline?: boolean }> = ({
  label,
  value,
  mono = true,
  multiline = false,
}) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // Clipboard can be blocked; the value stays selectable on screen.
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="flex items-start justify-between gap-3">
        <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{label}</p>
        <button
          onClick={copy}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-bold text-slate-700 hover:bg-slate-50"
          style={{ touchAction: 'manipulation' }}
        >
          {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <p
        className={`mt-1.5 break-all text-[13px] leading-relaxed text-slate-800 ${mono ? 'font-mono' : 'font-semibold'} ${
          multiline ? 'whitespace-pre-wrap' : ''
        }`}
      >
        {value}
      </p>
    </div>
  );
};

interface ConfigState {
  ok?: boolean;
  secretConfigured?: boolean;
  secretFingerprint?: string | null;
  secretLength?: number;
  webhookUrl?: string;
  error?: string;
}

interface TestState {
  ok?: boolean;
  httpStatus?: number;
  latencyMs?: number;
  response?: any;
  eventId?: string;
  storedEvent?: any;
  unauthorizedProbe?: { httpStatus: number; rejected: boolean };
  error?: string;
}

/**
 * What the endpoint itself says about its own configuration. This is read
 * WITHOUT an admin session, because it answers only one safe question:
 * "is the shared secret loaded on the server?" — no value, no hash, no length.
 */
interface ProbeState {
  configured: boolean;
  acceptsEvents: boolean;
  reachable: boolean;
}

/**
 * Result of the endpoint's own acceptance check ({selftest:true}). This is the
 * one thing "configured: true" cannot tell us: whether a correctly-signed
 * delivery is actually ACCEPTED end to end, or refused upstream by the hosting
 * gateway before this project's code ever runs.
 */
interface SelfTestState {
  accepted?: boolean;
  configured?: boolean;
  httpStatus?: number;
  result?: string | null;
  response?: any;
  wrongSecretStatus?: number;
  wrongSecretRejected?: boolean;
  deliveries24h?: number | null;
  rejections24h?: number | null;
  platformTokenRequired?: boolean;
  error?: string;
}

const AdminWebhookSetup: React.FC = () => {
  const { lang } = useI18n();
  const es = lang === 'es';
  const [cfg, setCfg] = useState<ConfigState | null>(null);
  const [probe, setProbe] = useState<ProbeState | null>(null);
  const [loadingCfg, setLoadingCfg] = useState(true);
  const [test, setTest] = useState<TestState | null>(null);
  const [testing, setTesting] = useState(false);
  const [self, setSelf] = useState<SelfTestState | null>(null);
  const [selfTesting, setSelfTesting] = useState(false);
  const [plan, setPlan] = useState('basic');

  const runSelfTest = async () => {
    setSelfTesting(true);
    setSelf(null);
    const { data, error } = await supabase.functions.invoke('payment-webhook', {
      body: { selftest: true },
    });
    setSelf(error ? { error: error.message } : (data as SelfTestState));
    setSelfTesting(false);
  };

  /**
   * DP0xx codes come from the hosting gateway, not from payment-webhook. That
   * distinction matters enormously: a DP003 means the delivery was refused
   * before any of our code ran, so there is no delivery record to find and the
   * fix is a missing header in the CRM — not a wrong shared secret.
   */
  const gatewayRefused =
    self?.platformTokenRequired === true ||
    String(self?.response?.code || '').startsWith('DP0');

  /**
   * TWO INDEPENDENT READS, because they answer different questions and fail
   * for different reasons:
   *
   *   1. payment-webhook {probe:true}  — unauthenticated. Is the secret set?
   *   2. payment-recovery {config}     — admin only. What is its fingerprint?
   *
   * THE BUG THIS FIXES: the panel used to derive "the shared secret is
   * missing — the webhook will reject everything" purely from read #2. Any
   * admin-auth failure (expired session, non-admin viewer, transient network
   * error) produced `data: null`, `secretConfigured: undefined` and therefore
   * the same red launch-blocking alarm as a genuinely unset secret. Those are
   * completely different situations and are now reported separately.
   */
  const loadConfig = useCallback(async () => {
    setLoadingCfg(true);
    const [probeRes, cfgRes] = await Promise.all([
      supabase.functions.invoke('payment-webhook', { body: { probe: true } }),
      supabase.functions.invoke('payment-recovery', { body: { action: 'config' } }),
    ]);

    const p = probeRes.data as Record<string, unknown> | null;
    setProbe({
      configured: p?.configured === true,
      acceptsEvents: p?.acceptsEvents === true,
      reachable: p?.ok === true,
    });
    setCfg(cfgRes.error ? { error: cfgRes.error.message } : cfgRes.data);
    setLoadingCfg(false);
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const runTest = async () => {
    setTesting(true);
    setTest(null);
    const { data, error } = await supabase.functions.invoke('payment-recovery', {
      body: { action: 'test', plan },
    });
    setTest(error ? { error: error.message } : data);
    setTesting(false);
  };

  // The endpoint's own answer wins. The admin read only adds the fingerprint.
  const secretOk = probe?.configured === true || cfg?.secretConfigured === true;
  // Probe unreachable AND no admin answer -> we genuinely do not know. Saying
  // "missing" here would be a guess, and a launch-blocking one.
  const statusUnknown = !loadingCfg && !secretOk && probe?.reachable !== true;
  const fingerprintBlocked = secretOk && cfg?.secretConfigured !== true;

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="flex items-center gap-2 text-xl font-extrabold text-slate-900">
            <Webhook className="h-5 w-5 text-orange-500" />
            {es ? 'Webhook de pagos — valores exactos' : 'Payment webhook — exact values'}
          </h2>
          <p className="mt-1 max-w-3xl text-sm text-slate-500">
            {es
              ? 'Pegue estos valores en el CRM. Solo un evento verificado en este endpoint activa un plan y otorga créditos: llegar a la página de retorno en el navegador nunca otorga nada.'
              : 'Paste these values into the CRM. Only a verified event on this endpoint activates a plan and grants credits — reaching the return page in a browser never grants anything.'}
          </p>
        </div>
        <button
          onClick={loadConfig}
          disabled={loadingCfg}
          className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loadingCfg ? 'animate-spin' : ''}`} />
          {es ? 'Revisar' : 'Re-check'}
        </button>
      </div>

      {/* ---------- secret status: configured / missing / cannot verify ---------- */}
      <div
        className={`mt-5 flex flex-wrap items-center gap-3 rounded-2xl border p-4 ${
          loadingCfg
            ? 'border-slate-200 bg-white'
            : secretOk
            ? 'border-emerald-200 bg-emerald-50/70'
            : statusUnknown
            ? 'border-amber-200 bg-amber-50/70'
            : 'border-red-200 bg-red-50/70'
        }`}
      >
        {loadingCfg ? (
          <RefreshCw className="h-6 w-6 animate-spin text-slate-300" />
        ) : secretOk ? (
          <ShieldCheck className="h-6 w-6 text-emerald-600" />
        ) : (
          <ShieldAlert className={`h-6 w-6 ${statusUnknown ? 'text-amber-600' : 'text-red-600'}`} />
        )}
        <div className="min-w-[220px] flex-1">
          <p className="text-sm font-extrabold text-slate-900">
            {loadingCfg
              ? es
                ? 'Comprobando el secreto compartido…'
                : 'Checking the shared secret…'
              : secretOk
              ? es
                ? 'El secreto compartido está configurado en el servidor — el webhook acepta eventos'
                : 'The shared secret is configured on the server — the webhook accepts events'
              : statusUnknown
              ? es
                ? 'No se pudo comprobar la configuración (esto NO significa que falte el secreto)'
                : 'Could not verify the configuration — this does NOT mean the secret is missing'
              : es
              ? 'Falta el secreto compartido — el webhook rechazará todo'
              : 'The shared secret is missing — the webhook will reject everything'}
          </p>
          {secretOk && (
            <p className="mt-0.5 text-xs font-semibold text-emerald-800">
              {cfg?.secretConfigured === true ? (
                <>
                  {es ? 'Huella' : 'Fingerprint'}: <span className="font-mono">{cfg?.secretFingerprint}</span> ·{' '}
                  {cfg?.secretLength} {es ? 'caracteres' : 'characters'} ·{' '}
                  {es
                    ? 'compare esta huella con el valor pegado en el CRM'
                    : 'compare this fingerprint against the value pasted in the CRM'}
                </>
              ) : (
                <>
                  {es
                    ? 'Verificado por el propio endpoint. Inicie sesión como administrador para ver la huella y compararla con el CRM.'
                    : 'Confirmed by the endpoint itself. Sign in as an administrator to see the fingerprint and compare it with the CRM.'}
                </>
              )}
            </p>
          )}
          {statusUnknown && (
            <p className="mt-0.5 text-xs font-semibold text-amber-800">
              {es
                ? 'El endpoint no respondió a la comprobación. Vuelva a revisar; si esto persiste, revise el estado de las funciones antes de cambiar el secreto.'
                : 'The endpoint did not answer the probe. Re-check; if it persists, look at function health before changing the secret.'}
            </p>
          )}
          {fingerprintBlocked && cfg?.error && (
            <p className="mt-0.5 text-xs font-semibold text-slate-500">
              {es ? 'Detalle de la lectura de administrador' : 'Admin read detail'}: {cfg.error}
            </p>
          )}
          {!loadingCfg && secretOk && probe?.acceptsEvents === false && (
            <p className="mt-0.5 text-xs font-semibold text-red-700">
              {es
                ? 'El secreto está configurado pero el almacenamiento de eventos no está disponible — los eventos no se registrarían.'
                : 'The secret is set but event storage is unavailable — deliveries would not be recorded.'}
            </p>
          )}
        </div>
      </div>


      {/* ---------- the exact values ---------- */}
      <div className="mt-5 grid gap-3 lg:grid-cols-2">
        <CopyRow label={es ? '1. URL de producción' : '1. Production webhook URL'} value={PAYMENT_WEBHOOK_URL} />
        <CopyRow label={es ? '2. Método HTTP' : '2. HTTP method'} value={WEBHOOK_METHOD} />
      </div>

      {/* ---------- ALL FOUR HEADERS (the launch blocker) ----------
          A delivery carrying only x-webhook-secret is refused by the hosting
          gateway with 401 DP003_INVALID_TOKEN BEFORE payment-webhook runs, so
          nothing is logged, no membership activates and no credits are granted
          even though the card was charged. The two token headers are therefore
          part of the contract, not an optional extra. */}
      <div className="mt-5 rounded-2xl border border-amber-300 bg-amber-50/70 p-4">
        <p className="text-sm font-extrabold text-slate-900">
          {es
            ? '3. Los CUATRO encabezados son obligatorios'
            : '3. All FOUR headers are required'}
        </p>
        <p className="mt-1 text-[13px] leading-relaxed text-amber-900">
          {es
            ? 'Con solo x-webhook-secret la puerta de enlace responde 401 DP003_INVALID_TOKEN antes de que GIGGONE vea el pago: la tarjeta se cobra y nada se activa. El token apikey / Authorization es publicable (ya viaja en el navegador) y no otorga nada por sí solo.'
            : 'With only x-webhook-secret the hosting gateway answers 401 DP003_INVALID_TOKEN before GIGGONE sees the payment — the card is charged and nothing activates. The apikey / Authorization token is publishable (it already ships in the browser) and grants nothing on its own.'}
        </p>
        <div className="mt-3 grid gap-3">
          {WEBHOOK_HEADERS.map((h, i) => (
            <div key={h.key}>
              <CopyRow
                label={`${es ? 'Encabezado' : 'Header'} ${i + 1} — ${h.key}${
                  h.secret ? (es ? ' (privado)' : ' (private)') : ''
                }`}
                value={h.value}
              />
              <p className="mt-1 px-1 text-[12px] leading-relaxed text-slate-600">{h.why}</p>
            </div>
          ))}
        </div>
        <p className="mt-3 text-[12px] font-semibold text-slate-600">
          {es
            ? `El valor de ${WEBHOOK_HEADER_NAME} es el texto guardado como ${WEBHOOK_SECRET_ENV}. Content-Type debe ser ${WEBHOOK_CONTENT_TYPE}.`
            : `The ${WEBHOOK_HEADER_NAME} value is the string stored as ${WEBHOOK_SECRET_ENV}. Content-Type must be ${WEBHOOK_CONTENT_TYPE}.`}
        </p>
      </div>

      {/* ---------- acceptance self-test ---------- */}
      <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-4">
        <p className="text-sm font-extrabold text-slate-900">
          {es ? '4. ¿Acepta entregas de verdad?' : '4. Does it actually accept deliveries?'}
        </p>
        <p className="mt-1 text-[13px] leading-relaxed text-slate-500">
          {es
            ? 'El endpoint se envía un evento a sí mismo con el secreto real y muestra lo que respondió. No requiere sesión de administrador. El destinatario es una dirección .invalid reservada: no se otorga ningún crédito y no existe ninguna tarjeta.'
            : 'The endpoint posts one event to itself with the live secret and shows exactly what came back. No admin session needed. The recipient is a reserved .invalid address, so no credit is granted and no card is involved.'}
        </p>
        <button
          onClick={runSelfTest}
          disabled={selfTesting}
          className="mt-3 inline-flex min-h-[48px] items-center gap-2 rounded-xl border border-slate-900 px-5 py-3 text-sm font-bold text-slate-900 hover:bg-slate-50 disabled:opacity-50"
          style={{ touchAction: 'manipulation' }}
        >
          {selfTesting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {selfTesting
            ? es
              ? 'Comprobando…'
              : 'Checking…'
            : es
            ? 'Comprobar aceptación'
            : 'Run acceptance check'}
        </button>

        {self && (
          <div
            className={`mt-3 rounded-xl border p-3 ${
              self.accepted ? 'border-emerald-200 bg-emerald-50/70' : 'border-red-200 bg-red-50/70'
            }`}
          >
            <p className="text-[13px] font-extrabold text-slate-900">
              {self.accepted
                ? es
                  ? `Aceptado — HTTP ${self.httpStatus} · ${self.result || 'procesado'}`
                  : `Accepted — HTTP ${self.httpStatus} · ${self.result || 'processed'}`
                : gatewayRefused
                ? es
                  ? 'La puerta de enlace rechazó la entrega: falta el token de plataforma'
                  : 'The gateway refused the delivery — platform token missing'
                : es
                ? `No aceptado — HTTP ${self.httpStatus}`
                : `Not accepted — HTTP ${self.httpStatus}`}
            </p>
            {gatewayRefused && (
              <p className="mt-1 text-[12px] font-semibold leading-relaxed text-red-800">
                {es
                  ? 'DP003_INVALID_TOKEN llega ANTES del código de GIGGONE. Añada los encabezados apikey y Authorization de arriba al webhook del CRM; el secreto compartido solo no es suficiente.'
                  : 'DP003_INVALID_TOKEN happens BEFORE GIGGONE\u2019s code runs. Add the apikey and Authorization headers shown above to the CRM webhook — the shared secret alone is not enough.'}
              </p>
            )}
            <ul className="mt-2 space-y-1 text-[12px] text-slate-700">
              <li>
                <b>{es ? 'Secreto cargado' : 'Secret loaded'}:</b>{' '}
                {self.configured ? (es ? 'sí' : 'yes') : es ? 'no' : 'no'}
              </li>
              <li>
                <b>{es ? 'Secreto incorrecto rechazado' : 'Wrong secret rejected'}:</b>{' '}
                {self.wrongSecretRejected ? `yes (HTTP ${self.wrongSecretStatus})` : 'no'}
              </li>
              <li>
                <b>{es ? 'Entregas en 24 h' : 'Deliveries in the last 24h'}:</b>{' '}
                {self.deliveries24h ?? '—'} · <b>{es ? 'rechazos' : 'rejections'}:</b>{' '}
                {self.rejections24h ?? '—'}
              </li>
              <li>
                <b>{es ? 'Créditos otorgados' : 'Credits granted'}:</b> 0
              </li>
            </ul>
            {self.deliveries24h === 0 && self.rejections24h === 0 && (
              <p className="mt-2 text-[12px] font-semibold text-slate-600">
                {es
                  ? 'Cero entregas y cero rechazos: el CRM no está llamando a este endpoint en absoluto.'
                  : 'Zero deliveries and zero rejections — the CRM is not calling this endpoint at all.'}
              </p>
            )}
          </div>
        )}
      </div>


      {/* ---------- payload ---------- */}
      <h3 className="mt-8 flex items-center gap-2 text-sm font-extrabold uppercase tracking-wide text-slate-900">
        <ListChecks className="h-4 w-4 text-slate-400" />
        {es ? '5. Cuerpo JSON requerido' : '5. Required JSON payload'}
      </h3>
      <div className="mt-3 flex flex-wrap gap-2">
        {Object.keys(WEBHOOK_PLAN_CREDITS).map((code) => (
          <button
            key={code}
            onClick={() => setPlan(code)}
            className={`rounded-xl px-4 py-2 text-sm font-bold capitalize transition-colors ${
              plan === code ? 'bg-slate-900 text-white' : 'border border-slate-300 text-slate-700 hover:bg-slate-50'
            }`}
          >
            {code} · {money(WEBHOOK_PLAN_CENTS[code])} · {WEBHOOK_PLAN_CREDITS[code]}{' '}
            {es ? 'créditos' : 'credits'}
          </button>
        ))}
      </div>
      <div className="mt-3">
        <CopyRow
          label={es ? `Cuerpo para ${plan}` : `Body for ${plan}`}
          value={webhookPayloadTemplate(plan)}
          multiline
        />
      </div>

      <div className="mt-3 grid gap-3 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">
            {es ? 'Campos obligatorios' : 'Required fields'}
          </p>
          <ul className="mt-2 space-y-1.5 text-[13px] text-slate-700">
            {REQUIRED_WEBHOOK_FIELDS.map((f) => (
              <li key={f.field}>
                <span className="font-mono font-bold text-slate-900">{f.field}</span> — {f.note}
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">
            {es ? 'Campos opcionales (se guardan)' : 'Optional fields (stored for reconciliation)'}
          </p>
          <ul className="mt-2 space-y-1.5 text-[13px] text-slate-700">
            {OPTIONAL_WEBHOOK_FIELDS.map((f) => (
              <li key={f.field}>
                <span className="font-mono font-bold text-slate-900">{f.field}</span> — {f.note}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* ---------- triggers ---------- */}
      <div className="mt-3 grid gap-3 lg:grid-cols-3">
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
          <p className="text-[11px] font-bold uppercase tracking-wide text-emerald-700">
            {es ? 'Activan el plan (primer pago)' : 'Activate the plan (first payment)'}
          </p>
          <p className="mt-2 font-mono text-[12px] leading-relaxed text-emerald-900">
            {SUCCESS_EVENT_TYPES.join(', ')}
          </p>
        </div>
        <div className="rounded-xl border border-sky-200 bg-sky-50/60 p-4">
          <p className="text-[11px] font-bold uppercase tracking-wide text-sky-700">
            {es ? 'Renuevan (reinician créditos)' : 'Renew (reset the monthly credits)'}
          </p>
          <p className="mt-2 font-mono text-[12px] leading-relaxed text-sky-900">{RENEWAL_EVENT_TYPES.join(', ')}</p>
        </div>
        <div className="rounded-xl border border-red-200 bg-red-50/60 p-4">
          <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-red-700">
            <CircleSlash className="h-3.5 w-3.5" /> {es ? 'Nunca otorgan créditos' : 'Never grant credits'}
          </p>
          <p className="mt-2 font-mono text-[12px] leading-relaxed text-red-900">
            {NON_GRANTING_EVENT_TYPES.join(', ')}
          </p>
        </div>
      </div>

      {/* ---------- return urls ---------- */}
      <h3 className="mt-8 text-sm font-extrabold uppercase tracking-wide text-slate-900">
        {es ? '6. URL de retorno después del pago' : '6. Post-payment return URL'}
      </h3>
      <p className="mt-1 max-w-3xl text-sm text-slate-500">
        {es
          ? 'Pegue esto en el campo de redirección del enlace de pago. El cliente vuelve a GIGGONE, ve la activación y continúa automáticamente a Proyectos.'
          : 'Paste this into the payment link\u2019s redirect field. The customer returns to GIGGONE, sees the activation and automatically continues to Projects.'}
      </p>

      <div className="mt-3 grid gap-3 lg:grid-cols-2">
        {Object.keys(WEBHOOK_PLAN_CREDITS).map((code) => (
          <React.Fragment key={code}>
            <CopyRow label={`${code} — English`} value={returnUrlFor(code, 'en')} />
            <CopyRow label={`${code} — Español`} value={returnUrlFor(code, 'es')} />
          </React.Fragment>
        ))}
      </div>

      {/* ---------- safe test ---------- */}
      <h3 className="mt-8 text-sm font-extrabold uppercase tracking-wide text-slate-900">
        {es ? '7. Prueba segura del webhook' : '7. Safe webhook test'}
      </h3>
      <p className="mt-1 max-w-3xl text-sm text-slate-500">
        {es
          ? 'Envía un evento sintético al endpoint real con el secreto real. El correo usado nunca puede coincidir con una cuenta, así que no se otorga ningún crédito y no se cobra ninguna tarjeta. También comprueba que un secreto incorrecto reciba 401.'
          : 'Sends a synthetic event to the real endpoint with the real secret. The email used can never match an account, so no credit is granted and no card is charged. It also proves a wrong secret is rejected with 401.'}
      </p>
      <button
        onClick={runTest}
        disabled={testing || !secretOk}
        className="mt-3 inline-flex min-h-[48px] items-center gap-2 rounded-xl bg-slate-900 px-5 py-3 text-sm font-bold text-white hover:bg-slate-800 disabled:opacity-50"
        style={{ touchAction: 'manipulation' }}
      >
        {testing ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        {testing
          ? es
            ? 'Enviando…'
            : 'Sending…'
          : es
          ? 'Enviar evento de prueba seguro'
          : 'Send safe test event'}
      </button>

      {test && (
        <div
          className={`mt-4 rounded-2xl border p-4 ${
            test.ok ? 'border-emerald-200 bg-emerald-50/70' : 'border-red-200 bg-red-50/70'
          }`}
        >
          <p className="text-sm font-extrabold text-slate-900">
            {test.ok
              ? es
                ? `Entregado — HTTP ${test.httpStatus} en ${test.latencyMs} ms`
                : `Delivered — HTTP ${test.httpStatus} in ${test.latencyMs} ms`
              : es
              ? 'La entrega falló'
              : 'Delivery failed'}
          </p>
          <ul className="mt-2 space-y-1 text-[13px] text-slate-700">
            <li>
              <b>{es ? 'Resultado' : 'Result'}:</b>{' '}
              <span className="font-mono">{String(test.response?.result || test.error || '—')}</span>
            </li>
            <li>
              <b>{es ? 'Evento guardado' : 'Event stored'}:</b>{' '}
              {test.storedEvent ? (es ? 'sí' : 'yes') : es ? 'no' : 'no'}{' '}
              <span className="font-mono text-slate-500">{test.eventId}</span>
            </li>
            <li>
              <b>{es ? 'Créditos otorgados' : 'Credits granted'}:</b> 0
            </li>
            <li>
              <b>{es ? 'Secreto incorrecto rechazado' : 'Wrong secret rejected'}:</b>{' '}
              {test.unauthorizedProbe?.rejected ? `yes (HTTP ${test.unauthorizedProbe.httpStatus})` : 'no'}
            </li>
          </ul>
        </div>
      )}

      {/* ---------- click by click ---------- */}
      <h3 className="mt-10 text-sm font-extrabold uppercase tracking-wide text-slate-900">
        {es ? '8. Configuración del CRM, clic por clic' : '8. CRM setup, click by click'}
      </h3>
      <div className="mt-3 space-y-3">
        {CRM_SETUP_STEPS.map((step) => (
          <div key={step.title} className="rounded-2xl border border-slate-200 bg-white p-5">
            {/* brandCase(): the GoHighLevel setup instructions are long-form prose,
                so the wordmark is normalised at the render boundary rather than
                retyped in every step (see src/lib/brand.ts). */}
            <p className="text-base font-extrabold text-slate-900">{brandCase(step.title)}</p>
            <ol className="mt-2 list-decimal space-y-1.5 pl-5 text-[13px] leading-relaxed text-slate-700">
              {step.clicks.map((c) => (
                <li key={c}>{brandCase(c)}</li>
              ))}
            </ol>
          </div>
        ))}
      </div>


      <a
        href="https://help.gohighlevel.com/support/solutions/articles/155000002533-payment-links"
        target="_blank"
        rel="noopener noreferrer"
        className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-orange-600 underline"
      >
        <ExternalLink className="h-4 w-4" />
        {es ? 'Documentación de enlaces de pago del CRM' : 'CRM payment-link documentation'}
      </a>
    </div>
  );
};

export default AdminWebhookSetup;
